/**
 * Navigation Prompt Hook
 *
 * This module provides a hook that prompts users with a confirmation dialog
 * when they attempt to navigate away from a page with unsaved changes.
 *
 * Features:
 * - Blocks navigation until user confirms
 * - Conditional activation based on form state
 * - Works with React Router navigation
 * - Can be bypassed when changes are successfully saved
 *
 * This is particularly useful for forms where users might accidentally
 * navigate away and lose their work.
 *
 * @module hooks/usePrompt
 */

import { useContext, useCallback, useEffect } from "react";
import { UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";

/**
 * Internal helper hook that handles the actual navigation blocking
 *
 * @param {Function} blocker - Function to call when navigation is attempted
 * @param {boolean} [when=true] - Whether the blocker is active
 * @param {boolean} [isSubmitSuccessfully=false] - Whether to bypass the blocker (e.g., after successful form submission)
 */
function useBlocker(blocker, when = true, isSubmitSuccessfully = false) {
  // Access React Router's navigation context
  const { navigator } = useContext(NavigationContext);

  useEffect(() => {
    // Don't block if condition is false or form was successfully submitted
    if (!when || isSubmitSuccessfully) return;

    // Set up navigation blocker
    const unblock = navigator.block((tx) => {
      // Create a transaction that automatically unblocks when retried
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock();
          tx.retry();
        }
      };

      // Call the blocker with the enhanced transaction
      blocker(autoUnblockingTx);
    });

    // Clean up the blocker when component unmounts or dependencies change
    return unblock;
  }, [navigator, blocker, when, isSubmitSuccessfully]);
}
/**
 * Hook to show a confirmation dialog when navigating away from a page
 *
 * @param {string} message - Message to show in the confirmation dialog
 * @param {boolean} [when=true] - Condition for showing the prompt (typically based on form dirty state)
 * @param {boolean} [isSubmitSuccessfully=false] - Whether to bypass the prompt (e.g., after successful save)
 */
export default function usePrompt(
  message,
  when = true,
  isSubmitSuccessfully = false
) {
  // Create a blocker function that shows a confirmation dialog
  const blocker = useCallback(
    (tx) => {
      // If user confirms, allow the navigation to proceed
      if (window.confirm(message)) tx.retry();
      // Otherwise, navigation is cancelled
    },
    [message]
  );

  // Use the blocker with the specified conditions
  useBlocker(blocker, when, isSubmitSuccessfully);
}
